import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { getPerf, getPublishStatus, messageFor, perfKey, publishStatusKey } from "./api";
import { attentionRows, formatWhen, publishLine, scoreLine, type AttentionRow } from "./format";

/**
 * The publish state, first on the screen (UX W1 rank 1). Read-only in this
 * build: the Publish action ships with Versions (MK-13), and the scope note is
 * stated here so nobody reads "marketing" into a global publish (G-2).
 */
function PublishLine({ areaName }: { areaName: string }) {
  const status = useQuery({ queryKey: publishStatusKey, queryFn: getPublishStatus });
  if (status.isPending) return <Skeleton className="h-12 w-full" />;
  if (status.isError) {
    return (
      <Alert>
        <AlertTitle>Publish status unavailable</AlertTitle>
        <AlertDescription>{messageFor(status.error, areaName, "the CVCE config store")}</AlertDescription>
      </Alert>
    );
  }
  const data = status.data.data;
  const published = formatWhen(data.live_published_at);
  return (
    <Card className="bg-ks-purple-4">
      <CardContent className="flex flex-col gap-1 py-3">
        <p className="text-base font-semibold text-ks-ink">{publishLine(data)}</p>
        <p className="text-sm text-ks-ink">
          {data.live_version !== null && `Live version v${data.live_version}${published ? ` · published ${published} UTC` : ""}. `}
          Publishing puts ALL CVCE config live, including any CX changes. Publish from the Versions tab when it ships.
        </p>
      </CardContent>
    </Card>
  );
}

function RowList({ title, rows, empty }: { title: string; rows: AttentionRow[]; empty: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-ks-ink">{empty}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-ks-grey-4">
            {rows.map((row) => (
              <li key={`${row.kind}:${row.label}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span className="flex items-center gap-2 text-ks-ink">
                  {row.label}
                  <Badge variant="outline">{row.kind === "skeleton" ? "Skeleton" : "Snippet"}</Badge>
                </span>
                <span className={row.low_sample ? "text-ks-ink italic" : "font-medium text-ks-ink tabular-nums"}>
                  {scoreLine(row)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Overview (UX S1): is anything wrong, and is there unpublished work. The two
 * reads fail independently (the partial state): publish status keeps its own
 * card and error, and the perf lists carry theirs.
 */
export function OverviewTab({ areaName }: { areaName: string }) {
  const perf = useQuery({ queryKey: perfKey, queryFn: getPerf });

  let body;
  if (perf.isPending) {
    body = <Skeleton className="h-40 w-full" />;
  } else if (perf.isError) {
    body = (
      <Alert>
        <AlertTitle>Marketing scores unavailable</AlertTitle>
        <AlertDescription>{messageFor(perf.error, areaName, "CVCE telemetry")}</AlertDescription>
      </Alert>
    );
  } else if (!perf.data.data.available) {
    body = (
      <Alert>
        <AlertTitle>Marketing scores unavailable</AlertTitle>
        <AlertDescription>We can't reach CVCE telemetry right now. Try again shortly.</AlertDescription>
      </Alert>
    );
  } else if (!perf.data.data.total_drafts) {
    body = <p className="text-sm text-ks-ink">No marketing signals yet. Scores appear once the engine drafts its first assets.</p>;
  } else {
    const { needsAttention, workingWell } = attentionRows(perf.data.data);
    const window = perf.data.data.window;
    body = (
      <>
        <RowList title="Needs attention" rows={needsAttention} empty="Nothing is declining. No skeleton or snippet score is trending down." />
        <RowList title="Working well" rows={workingWell} empty="No skeleton or snippet score is trending up yet." />
        {window ? (
          <p className="text-sm text-ks-ink">
            Each score averages its latest {window} scored drafts. The change compares the newer half of those drafts
            with the older half.
          </p>
        ) : null}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PublishLine areaName={areaName} />
      {body}
    </div>
  );
}
